import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { getProduct, fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { OrganicBadge } from "@/components/OrganicBadge";
import { Badge } from "@/components/ui/badge";
import { ShoppingCart, ArrowLeft, ImageOff, Truck, Wallet, ShieldCheck, Minus, Plus, Zap, FileCheck2 } from "lucide-react";
import { toast } from "sonner";

const ProductPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, lang, addToCart } = useApp();
  const [product, setProduct] = useState(null);
  const [qty, setQty] = useState(1);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getProduct(id)
      .then(setProduct)
      .catch(() => toast.error("Product not found"))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="grid md:grid-cols-2 gap-8">
          <div className="aspect-square bg-[#EFEBE2] rounded-2xl animate-pulse" />
          <div className="space-y-4">
            <div className="h-8 bg-[#EFEBE2] rounded animate-pulse" />
            <div className="h-4 bg-[#EFEBE2] rounded w-1/2 animate-pulse" />
            <div className="h-24 bg-[#EFEBE2] rounded animate-pulse" />
          </div>
        </div>
      </div>
    );
  }

  if (!product) return null;

  const name = lang === "hi" ? product.name_hi : product.name_en;
  const desc = lang === "hi" ? product.description_hi : product.description_en;
  const img = fileUrl(product.image_path);
  const outOfStock = product.stock <= 0;

  const handleAdd = () => {
    addToCart(product, qty);
    toast.success(lang === "hi" ? `${qty} × कार्ट में जोड़ा गया` : `${qty} × added to cart`);
  };

  const handleBuyNow = () => {
    addToCart(product, qty);
    navigate("/checkout");
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-10" data-testid="product-page">
      <button
        onClick={() => navigate(-1)}
        className="flex items-center gap-1.5 text-sm text-slate-600 hover:text-[#2D5A27] mb-6"
        data-testid="back-btn"
      >
        <ArrowLeft className="w-4 h-4" /> {t("continueShopping")}
      </button>

      <div className="grid md:grid-cols-2 gap-8 md:gap-12">
        <div className="bg-white border border-[#E2E8F0] rounded-2xl overflow-hidden aspect-square relative">
          {img ? (
            <img src={img} alt={name} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex flex-col items-center justify-center text-slate-400 leaf-pattern">
              <ImageOff className="w-16 h-16 opacity-40" />
            </div>
          )}
          <div className="absolute bottom-4 left-4">
            <OrganicBadge category={product.category} size="lg" />
          </div>
          <div className="absolute top-4 left-4">
            <Badge variant="outline" className="bg-white border-[#E2E8F0] uppercase tracking-wider text-xs font-semibold">
              {t(product.category)}
            </Badge>
          </div>
        </div>

        <div>
          <h1
            className={`font-heading font-bold text-3xl sm:text-4xl text-[#1E293B] leading-tight ${lang === "hi" ? "font-devanagari" : ""}`}
            data-testid="product-detail-name"
          >
            {name}
          </h1>
          <p className="text-slate-500 mt-2 text-sm">{product.unit}</p>

          <div className="mt-6 flex items-baseline gap-3">
            <span className="font-heading font-bold text-4xl text-[#2D5A27]" data-testid="product-detail-price">
              ₹{product.price.toFixed(0)}
            </span>
            <span className="text-sm text-slate-500">/ {product.unit}</span>
          </div>

          {desc && (
            <p className={`mt-5 text-slate-600 leading-relaxed ${lang === "hi" ? "font-devanagari" : ""}`}>
              {desc}
            </p>
          )}

          {product.certificate_path && (
            <a
              href={fileUrl(product.certificate_path)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="view-certificate-btn"
              className="mt-5 inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#F0FDF4] border border-[#86EFAC] text-[#15803D] text-sm font-semibold hover:bg-[#DCFCE7] transition-colors"
            >
              <FileCheck2 className="w-4 h-4" />
              {lang === "hi" ? "प्रमाणपत्र / लैब रिपोर्ट देखें" : "View Certificate / Lab Report"}
            </a>
          )}

          <div className="mt-6 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
            <div className="flex items-center border border-[#E2E8F0] rounded-full bg-white self-start">
              <button
                onClick={() => setQty(Math.max(1, qty - 1))}
                data-testid="qty-decrease"
                className="w-10 h-11 flex items-center justify-center rounded-l-full hover:bg-[#EFEBE2]"
              >
                <Minus className="w-4 h-4" />
              </button>
              <span className="w-10 text-center font-semibold" data-testid="qty-value">{qty}</span>
              <button
                onClick={() => setQty(Math.min(product.stock, qty + 1))}
                data-testid="qty-increase"
                className="w-10 h-11 flex items-center justify-center rounded-r-full hover:bg-[#EFEBE2]"
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>
            <div className="flex gap-2 flex-1">
              <Button
                onClick={handleAdd}
                disabled={outOfStock}
                data-testid="detail-add-to-cart"
                variant="outline"
                className="flex-1 h-11 rounded-full border-[#2D5A27] text-[#2D5A27] hover:bg-[#EFEBE2] font-semibold pressable"
              >
                <ShoppingCart className="w-4 h-4 mr-2" /> {t("addToCart")}
              </Button>
              <Button
                onClick={handleBuyNow}
                disabled={outOfStock}
                data-testid="detail-buy-now"
                className="flex-1 h-11 rounded-full bg-[#D97706] hover:bg-[#B45309] text-white font-semibold pressable"
              >
                <Zap className="w-4 h-4 mr-2 fill-white" /> {t("buyNow")}
              </Button>
            </div>
          </div>

          <div className="mt-8 grid grid-cols-3 gap-3">
            {[
              { icon: Truck, label: t("freeDelivery") },
              { icon: Wallet, label: t("codAvailable") },
              { icon: ShieldCheck, label: t("trustedBy") },
            ].map((f, i) => (
              <div key={i} className="p-3 rounded-xl bg-[#EFEBE2] text-center">
                <f.icon className="w-5 h-5 mx-auto text-[#2D5A27] mb-1" />
                <p className="text-[10px] sm:text-xs text-slate-700 font-medium leading-tight">{f.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductPage;
